import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Trash2,
  Edit2,
  UserPlus,
  Shield,
  User as UserIcon,
  Loader2,
  Users,
  Layers,
  Mail,
  Copy,
  RefreshCw,
  X,
  Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  getWorkspaceMembers,
  addWorkspaceMember,
  updateWorkspaceMember,
  removeWorkspaceMember,
  deleteUser,
  getWorkspaceInvites,
  cancelWorkspaceInvite,
  resendWorkspaceInvite,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useChannel } from '../context/ChannelContext';

const copyInviteLink = (invite) => {
  const link = `${window.location.origin}/invite/${invite.token}`;
  navigator.clipboard.writeText(link)
    .then(() => toast.success('Invite link copied'))
    .catch(() => toast.error('Could not copy. Link: ' + link));
};

const MAX_CHANNEL_NAMES = 3;

const isFullAccess = (member) =>
  member.workspaceRole === 'admin' || member.role === 'admin' || member.role === 'superadmin';

export function UserManagement() {
  const { user: currentUser, isAdmin } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { channels } = useChannel();

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Add member dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', role: 'member' });

  // Edit member dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editRole, setEditRole] = useState('member');
  const [editChannelIds, setEditChannelIds] = useState([]);

  // Remove / delete dialog
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removeMember, setRemoveMember] = useState(null);

  const fetchMembers = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [membersData, invitesData] = await Promise.all([
        getWorkspaceMembers(selectedWorkspaceId),
        getWorkspaceInvites(selectedWorkspaceId),
      ]);
      setMembers(membersData.members || []);
      setInvites(invitesData.invites || []);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleAddMember = async () => {
    if (!addForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await addWorkspaceMember(selectedWorkspaceId, { email: addForm.email, role: addForm.role });
      toast[res.emailSent === false ? 'warning' : 'success'](res.message || 'Invitation sent');
      setShowAddDialog(false);
      setAddForm({ email: '', role: 'member' });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMember = async () => {
    setSubmitting(true);
    try {
      // Admins see every channel, so channel access only applies to members
      const data = editRole === 'member' ? { role: editRole, channelIds: editChannelIds } : { role: editRole };
      await updateWorkspaceMember(selectedWorkspaceId, editMember.id, data);
      toast.success('Member updated');
      setShowEditDialog(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    setSubmitting(true);
    try {
      await removeWorkspaceMember(selectedWorkspaceId, removeMember.id);
      toast.success('Member removed from workspace');
      setShowRemoveDialog(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (member) => {
    setSubmitting(true);
    try {
      await deleteUser(member.id);
      toast.success('User deleted');
      setShowRemoveDialog(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvite = async (invite) => {
    try {
      await cancelWorkspaceInvite(selectedWorkspaceId, invite.id);
      toast.success('Invite cancelled');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invite');
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      await resendWorkspaceInvite(selectedWorkspaceId, invite.id);
      toast.success('Invite resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const openEditDialog = (member) => {
    setEditMember(member);
    setEditRole(member.workspaceRole || 'member');
    setEditChannelIds(member.channelIds || []);
    setShowEditDialog(true);
  };

  const toggleEditChannel = (channelId, checked) => {
    setEditChannelIds(prev => checked ? [...prev, channelId] : prev.filter(id => id !== channelId));
  };

  const renderChannelAccess = (member) => {
    if (isFullAccess(member)) {
      return <span className="text-zinc-400">All channels</span>;
    }
    const names = (member.channelIds || [])
      .map(id => channels.find(c => c.id === id)?.name)
      .filter(Boolean);
    if (names.length === 0) {
      return <span className="text-amber-500/80">No channels yet</span>;
    }
    const shown = names.slice(0, MAX_CHANNEL_NAMES).join(', ');
    const extra = names.length - MAX_CHANNEL_NAMES;
    return (
      <span className="text-zinc-400" title={names.join(', ')}>
        {shown}{extra > 0 && <span className="text-zinc-500"> +{extra} more</span>}
      </span>
    );
  };

  const openRemoveDialog = (member) => {
    setRemoveMember(member);
    setShowRemoveDialog(true);
  };

  if (!selectedWorkspaceId) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Layers className="w-12 h-12 text-zinc-600 mb-4" />
          <h3 className="text-lg font-medium text-zinc-300 mb-2">No workspace selected</h3>
          <p className="text-zinc-500">Select a workspace from the sidebar to manage its users</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100">User Management</h1>
          <p className="text-zinc-400 mt-1">
            Members of <span className="text-zinc-200 font-medium">{selectedWorkspace?.name}</span>
          </p>
        </div>
        <Button
          onClick={() => { setAddForm({ email: '', role: 'member' }); setShowAddDialog(true); }} data-testid="add-member-btn"
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Members List */}
      <div className="grid gap-3">
        {members.length === 0 && invites.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-zinc-300 mb-2">No members yet</h3>
              <p className="text-zinc-500 mb-4">Add users to this workspace to get started</p>
              <Button
                onClick={() => { setAddForm({ email: '', role: 'member' }); setShowAddDialog(true); }} data-testid="add-member-btn"
                variant="outline"
                className="border-zinc-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
          {members.map((member) => (
            <Card key={member.id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Avatar + Info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      member.workspaceRole === 'admin' ? 'bg-amber-500/10' : 'bg-emerald-500/10'
                    }`}>
                      {member.workspaceRole === 'admin' ? (
                        <Shield className="w-5 h-5 text-amber-500" />
                      ) : (
                        <UserIcon className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-zinc-100 truncate">{member.name}</span>
                        <Badge
                          className={`text-xs ${
                            member.workspaceRole === 'admin'
                              ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                              : 'bg-zinc-800 text-zinc-400 border-zinc-700'
                          }`}
                        >
                          {member.workspaceRole === 'admin' ? 'Workspace Admin' : 'Member'}
                        </Badge>
                        {currentUser?.id === member.id && (
                          <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-xs">
                            You
                          </Badge>
                        )}
                        {(member.role === 'admin' || member.role === 'superadmin') && (
                          <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                            Superadmin
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500 truncate">{member.email}</p>
                      <p className="flex items-center gap-1.5 text-xs mt-1 min-w-0" data-testid="member-channels-cell">
                        <Building2 className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                        <span className="truncate">{renderChannelAccess(member)}</span>
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  {currentUser?.id !== member.id && (
                    <div className="flex items-center gap-2 sm:ml-2 flex-shrink-0">
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(member)} className="text-zinc-400 hover:text-zinc-100" data-testid={`edit-member-${member.email}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit role and channels</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => openRemoveDialog(member)} className="text-zinc-400 hover:text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Remove from workspace</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {invites.map((invite) => (
            <Card key={invite.id} className="bg-zinc-900 border-zinc-800 border-dashed">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-zinc-400 truncate">{invite.email}</span>
                        <Badge className="text-xs bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                          Pending
                        </Badge>
                        <Badge className="text-xs bg-zinc-800 text-zinc-500 border-zinc-700 capitalize">
                          {invite.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-600">
                        {invite.emailSent === false ? 'Email not sent — copy the link and share it' : 'Invite sent · awaiting acceptance'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:ml-2 flex-shrink-0">
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => copyInviteLink(invite)} className="text-zinc-400 hover:text-emerald-400" data-testid="copy-invite-link">
                            <Copy className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copy invite link</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => handleResendInvite(invite)} className="text-zinc-400 hover:text-emerald-400">
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Resend invite</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => handleCancelInvite(invite)} className="text-zinc-400 hover:text-red-400">
                            <X className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancel invite</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </>
        )}
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Add Member</DialogTitle>
            <DialogDescription className="text-zinc-400">
              An invite will be sent by email to join <span className="font-medium text-zinc-200">{selectedWorkspace?.name}</span>. New users will be asked to set a password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email *</Label>
              <Input data-testid="invite-email-input"
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100"
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Workspace Role</Label>
              <Select value={addForm.role} onValueChange={(v) => setAddForm({ ...addForm, role: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button data-testid="send-invite-btn" onClick={handleAddMember} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Member</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update workspace role and channel access for <span className="font-medium text-zinc-200">{editMember?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Workspace Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Channels</Label>
              {editRole === 'admin' ? (
                <p className="text-sm text-zinc-500">Admins can access all channels</p>
              ) : channels.length === 0 ? (
                <p className="text-sm text-zinc-500">This workspace has no channels yet</p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-md border border-zinc-800 divide-y divide-zinc-800">
                  {channels.map((channel) => (
                    <label
                      key={channel.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50"
                    >
                      <Checkbox
                        checked={editChannelIds.includes(channel.id)}
                        onCheckedChange={(checked) => toggleEditChannel(channel.id, checked === true)}
                        className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 data-[state=checked]:text-white"
                        data-testid={`member-channel-checkbox-${channel.id}`}
                      />
                      <span className="text-sm text-zinc-200 truncate">{channel.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleUpdateMember} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Remove Member</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Remove <span className="font-medium text-zinc-200">{removeMember?.name}</span> from{' '}
              <span className="font-medium text-zinc-200">{selectedWorkspace?.name}</span>?
              Their account will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowRemoveDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            {isAdmin && (
              <Button
                onClick={() => handleDeleteUser(removeMember)}
                disabled={submitting}
                variant="outline"
                className="border-red-800 text-red-400 hover:bg-red-500/10"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Account'}
              </Button>
            )}
            <Button onClick={handleRemove} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UserManagement;
